import { Fragment } from 'react';
import { Formik, FieldArray } from 'formik';

import { useHeader } from 'context';
import { ButtonColors } from 'types';
import { useAppSelector } from 'store';
import { formatDate } from 'utils/date';
import { capitalize } from 'utils/string';
import { useListQuery } from 'store/api/users';
import { EventFormValues } from 'store/api/event';
import { isMobile, useAlert, useNavigate } from 'hooks';
import { useListQuery as useThemesQuery } from 'store/api/themes';
import { useListQuery as useProductsQuery } from 'store/api/product';
import { isAuthCompanyUser, selectAuthUserType } from 'store/slices/auth';
import { useListQuery as usePaymentMethodsQuery } from 'store/api/payment-methods';
import {
  handleFormSubmitWithFiles,
  makeSelectOptionsFromEntities,
} from 'utils/helpers';
import {
  Row,
  Col,
  Flex,
  Icon,
  Button,
  AddButton,
  FormFooter,
  InputField,
  RatingField,
  ButtonTypes,
  SelectField,
  SwitchField,
  DeleteButton,
  EllipsisText,
  CommonStyles,
  ErrorMessage,
  CheckboxField,
  SectionHeader,
  DatePickerField,
  TimePickerField,
  StatesSelectField,
  FileDropzoneField,
  FormPhotoFieldRow,
  EventTeamAssignSelects,
  AddressAutoCompleteInputField,
} from 'components';

import * as Styles from './styles';
import { FormProps } from './types';
import {
  validationSchema,
  getInitialValues,
  eventTypeOptions,
  paymentTypeOptions,
  transactionOptions,
  eventLocationTypeOptions,
} from './config';

export const Form = ({ data, onSubmit }: FormProps) => {
  const mobile = isMobile();
  const navigate = useNavigate();
  const authUserType = useAppSelector(selectAuthUserType);
  const isCompanyOwner = useAppSelector(isAuthCompanyUser);
  const { handleError } = useAlert();

  const { data: themes } = useThemesQuery();
  const { data: products } = useProductsQuery();
  const { data: paymentMethods } = usePaymentMethodsQuery({ status: 'active' });
  const { data: users } = useListQuery({
    limit: 10000,
    hideTeamMemberRoles: true,
  });

  const isEdit = Boolean(data);

  useHeader({
    showBackButton: true,
    pageName: `${isEdit ? 'Edit' : 'New'} event`,
  });

  const themeSelectOptions = makeSelectOptionsFromEntities(themes);
  const productSelectOptions = makeSelectOptionsFromEntities(products);
  const userSelectOptions = makeSelectOptionsFromEntities(users?.rows);
  const paymentMethodOptions = makeSelectOptionsFromEntities(paymentMethods);

  return (
    <Formik<EventFormValues>
      validationSchema={validationSchema}
      initialValues={getInitialValues(data)}
      onSubmit={handleFormSubmitWithFiles({
        data,
        handleError,
        onSubmit: (values) =>
          onSubmit({
            ...values,
            endDate: formatDate(values.endDate),
            startDate: formatDate(values.startDate),
          }),
        files: [
          {
            formFieldName: 'floorPlan',
            requestFieldName: 'floorPlanFileUrl',
          },
          {
            formFieldName: 'photo',
            requestFieldName: 'photoUrl',
          },
        ],
      })}
    >
      {({ dirty, errors, values, touched, isSubmitting, setFieldValue }) => {
        const datesError =
          (touched?.dates && errors.startDate) ||
          (touched?.dates && errors.endDate);
        const setZipcodeAndTax = (zip: string, taxRate: string) => {
          setFieldValue('zip', zip);
          setFieldValue('tax', +taxRate);
        };

        return (
          <CommonStyles.Form>
            <CommonStyles.FormInner>
              <FormPhotoFieldRow />

              <SectionHeader withBorder={false} title="Event information" />

              <Row>
                <Col col={3} colTablet={4}>
                  <InputField name="name" label="Name" placeholder="Name" />
                </Col>

                <Col col={3} colTablet={4}>
                  <DatePickerField
                    selectsRange
                    name="dates"
                    label="Select Dates"
                    minDate={new Date()}
                    placeholderText="Select Dates"
                    onChange={(dates) => {
                      setFieldValue('startDate', dates?.[0]);
                      setFieldValue('endDate', dates?.[1]);
                    }}
                  />
                  {datesError && <ErrorMessage>{datesError}</ErrorMessage>}
                </Col>
              </Row>

              <Row>
                <Col col={3} colTablet={6}>
                  <InputField
                    name="sellingDays"
                    label="Selling days"
                    placeholder="Selling days"
                  />
                </Col>

                <Col col={3} colTablet={6}>
                  <InputField
                    number
                    decimal
                    name="tax"
                    label="Tax, %"
                    placeholder="Tax, %"
                  />
                </Col>
              </Row>

              <Row>
                <Col col={3} colTablet={6}>
                  <AddressAutoCompleteInputField
                    zipFieldName="zip"
                    onTaxSelected={setZipcodeAndTax}
                  />
                </Col>

                <Col col={3} colTablet={6}>
                  <StatesSelectField isDisabled name="state" />
                </Col>

                <Col col={3} colTablet={6}>
                  <InputField
                    disabled
                    name="city"
                    label="City"
                    placeholder="City"
                  />
                </Col>

                <Col col={3} colTablet={6}>
                  <InputField
                    number
                    disabled
                    name="zip"
                    label="ZIP"
                    placeholder="ZIP"
                  />
                </Col>
              </Row>

              <SectionHeader withBorder={false} title="Contact information" />
              <Row>
                <Col col={3} colTablet={4}>
                  <InputField
                    name="contactName"
                    label="Contact Name"
                    placeholder="Contact Name"
                  />
                </Col>

                <Col col={3} colTablet={4}>
                  <InputField
                    type="tel"
                    label="Phone"
                    name="contactPhone"
                    placeholder="Phone"
                  />
                </Col>

                <Col col={3} colTablet={4}>
                  <InputField
                    label="Email"
                    name="contactEmail"
                    placeholder="Email"
                  />
                </Col>
              </Row>

              <SectionHeader
                withBorder={false}
                title="Event Theme & Products"
              />
              <Row>
                <Col col={3} colTablet={4}>
                  <SelectField
                    onlyValue
                    label="Theme"
                    name="themeId"
                    placeholder="Theme"
                    options={themeSelectOptions}
                  />
                </Col>

                <Col col={3} colTablet={4}>
                  <InputField name="link" label="Link" placeholder="Link" />
                </Col>

                <Col col={3} colTablet={4}>
                  <SelectField
                    onlyValue
                    label="Product"
                    name="productId"
                    placeholder="Select product"
                    options={productSelectOptions}
                  />
                </Col>
              </Row>

              <SectionHeader withBorder={false} title="Attendance" />

              <Row>
                <Col col={6} colTablet={6}>
                  <InputField
                    name="attendance"
                    label="Attendance"
                    placeholder="Add attendance"
                  />
                </Col>

                <Col col={6} colTablet={6}>
                  <InputField
                    name="vendors"
                    label="Vendors"
                    placeholder="Add vendors"
                  />
                </Col>
              </Row>

              <Row>
                <Col col={6} colTablet={6}>
                  <RatingField label="Event Rating" />
                </Col>

                <Col col={6} colTablet={6}>
                  <InputField
                    name="ratingMessage"
                    label="Event Rating Message"
                    placeholder="Add event rating message"
                  />
                </Col>
              </Row>

              <SectionHeader title="Locations" withBorder={false} />

              <FieldArray
                name="locations"
                render={({ push, remove }) => (
                  <>
                    {values.locations?.map((location, index) => (
                      <Fragment key={index}>
                        <Row>
                          <Col col={2} colTablet={4}>
                            <InputField
                              label="#"
                              placeholder="#"
                              name={`locations.${index}.number`}
                            />
                          </Col>
                          <Col col={2} colTablet={4}>
                            <InputField
                              number
                              decimal
                              label="$"
                              placeholder="$"
                              name={`locations.${index}.income`}
                            />
                          </Col>
                          <Col col={2}>
                            <Flex gap={8}>
                              <InputField
                                number
                                decimal
                                label="Length, ft"
                                placeholder="Length, ft"
                                name={`locations.${index}.length`}
                              />
                              <InputField
                                number
                                decimal
                                label="Width, ft"
                                placeholder="Width, ft"
                                name={`locations.${index}.width`}
                              />
                            </Flex>
                          </Col>

                          <Col col={2} colTablet={6}>
                            <SelectField
                              onlyValue
                              label="Location Type"
                              placeholder="Select location type"
                              options={eventLocationTypeOptions}
                              name={`locations.${index}.locationType`}
                            />
                          </Col>

                          <Col col={2} colTablet={6}>
                            <SelectField
                              onlyValue
                              label="Indoor/Outdoor"
                              options={eventTypeOptions}
                              placeholder="Select Indoor/Outdoor"
                              name={`locations.${index}.eventType`}
                            />
                          </Col>
                        </Row>

                        {(location.id || index > 0) && (
                          <Row>
                            <Col>
                              <DeleteButton
                                text="Remove location"
                                onClick={() => remove(index)}
                              />
                            </Col>
                          </Row>
                        )}
                      </Fragment>
                    ))}

                    <Row>
                      <Col>
                        <AddButton
                          text="Add location"
                          onClick={() =>
                            push({
                              number: '',
                              eventType: '',
                              locationType: '',
                            })
                          }
                        />
                      </Col>
                    </Row>
                  </>
                )}
              />

              <Row>
                <Col col={3} colTablet={4}>
                  <Flex gap={4} minWidth={0} alignItems="center">
                    <FileDropzoneField name="floorPlan" label="Floor Plan">
                      <Flex gap={4} minWidth={0} alignItems="center">
                        <Button text="Choose file" />

                        {values.floorPlan && (
                          <EllipsisText>
                            {values.floorPlan.file.name}
                          </EllipsisText>
                        )}
                      </Flex>
                    </FileDropzoneField>

                    {values.floorPlan && (
                      <Styles.DeleteFileButton
                        onClick={() => setFieldValue('floorPlan', null)}
                      />
                    )}
                  </Flex>
                </Col>

                <Col col={3} colTablet={4}>
                  <InputField
                    name="floorPlanLink"
                    label="Floor Plan Link"
                    leftIcon={<Icon.Link />}
                  />
                </Col>
                <Col col={3} colTablet={4}>
                  <InputField
                    number
                    decimal
                    name="finalPrice"
                    label="Final Price"
                    leftIcon={<Icon.CoinDollar />}
                  />
                </Col>
              </Row>

              {authUserType === 'ops' && (
                <Row>
                  <Col>
                    <InputField
                      type="textarea"
                      name="bookingTeamNotes"
                      label="Booking Team Notes"
                    />
                  </Col>
                </Row>
              )}

              <SectionHeader title="Payments" withBorder={false} />
              <FieldArray
                name="payments"
                render={({ push, remove }) => (
                  <>
                    {values.payments?.map((payment, index) => (
                      <Fragment key={index}>
                        <Row>
                          <Col col={2} colTablet={6}>
                            <SelectField
                              onlyValue
                              menuPlacement="top"
                              label="Payment Type"
                              placeholder="Payment Type"
                              options={paymentTypeOptions}
                              name={`payments.${index}.type`}
                            />
                          </Col>
                          <Col col={2} colTablet={6}>
                            <SelectField
                              onlyValue
                              menuPlacement="top"
                              label="Transaction"
                              placeholder="Transaction"
                              options={transactionOptions}
                              name={`payments.${index}.transaction`}
                            />
                          </Col>
                          <Col col={2} colTablet={6}>
                            <InputField
                              label="Notes"
                              placeholder="Notes"
                              name={`payments.${index}.notes`}
                            />
                          </Col>
                          <Col col={2} colTablet={6}>
                            <InputField
                              number
                              decimal
                              label="Amount"
                              placeholder="Amount"
                              leftIcon={<Icon.CoinDollar />}
                              name={`payments.${index}.amount`}
                            />
                          </Col>
                          <Col col={2} colTablet={6}>
                            <DatePickerField
                              label="Date"
                              placeholderText="Date"
                              name={`payments.${index}.date`}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col col={2} colTablet={6}>
                            <SelectField
                              onlyValue
                              menuPlacement="top"
                              label="Paid/Scheduled By"
                              options={userSelectOptions}
                              placeholder="Paid/Scheduled By"
                              name={`payments.${index}.paidById`}
                            />
                          </Col>
                          <Col col={3} colTablet={6}>
                            <Flex gap={16} alignItems="center">
                              <SelectField
                                onlyValue
                                menuPlacement="top"
                                label="Payment Method"
                                options={paymentMethodOptions}
                                name={`payments.${index}.paymentMethodId`}
                              />
                              <CheckboxField
                                label="Cashed"
                                style={{ marginTop: 16 }}
                                name={`payments.${index}.cashed`}
                              />
                            </Flex>
                          </Col>
                        </Row>

                        <Row>
                          <Col>
                            <DeleteButton
                              text="Remove payment"
                              onClick={() => remove(index)}
                            />
                          </Col>
                        </Row>
                      </Fragment>
                    ))}

                    <AddButton
                      text="Add payment"
                      onClick={() => push({ type: '', cashed: false })}
                    />
                  </>
                )}
              />

              <SectionHeader withBorder={false} title="Weekly Schedule" />
              <Flex column gap={8}>
                <FieldArray
                  name="weeklySchedule"
                  render={() =>
                    values?.weeklySchedule?.map((daySchedule, index) => (
                      <Styles.WeeklyScheduleContainer key={index}>
                        <Styles.WeeklyScheduleDay>
                          {capitalize(daySchedule.day)}
                        </Styles.WeeklyScheduleDay>
                        <TimePickerField
                          label="From"
                          name={`weeklySchedule.${index}.from`}
                        />
                        <TimePickerField
                          label="To"
                          name={`weeklySchedule.${index}.to`}
                        />
                      </Styles.WeeklyScheduleContainer>
                    ))
                  }
                />
              </Flex>

              {authUserType === 'client' && (
                <>
                  <SectionHeader title="Team" withBorder={false} />
                  <EventTeamAssignSelects
                    menuPlacement="top"
                    colProps={{ col: 3, colTablet: 6 }}
                  />

                  <Row>
                    <Col col={6} colTablet={6}>
                      <InputField
                        name="notes"
                        label="Notes"
                        placeholder="Notes"
                        type="textarea-autosize"
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col col={6} colTablet={6}>
                      <InputField
                        type="textarea"
                        name="notesForClient"
                        label="Notes For Client"
                      />
                    </Col>
                  </Row>
                </>
              )}

              {isCompanyOwner && (
                <>
                  <SectionHeader
                    withBorder={false}
                    title="Manual Credit Payment"
                  />
                  <Row>
                    <Col col={3}>
                      <SwitchField
                        label="Manual Credit Payment"
                        name="isManualCreditPaymentEnabled"
                      />
                    </Col>
                  </Row>
                </>
              )}
            </CommonStyles.FormInner>

            <FormFooter
              right={
                <CommonStyles.FormButtons>
                  <Button
                    text="Cancel"
                    fullWidth={mobile}
                    onClick={() => navigate(-1)}
                    colorType={ButtonColors.LightBlue}
                  />
                  <Button
                    fullWidth={mobile}
                    type={ButtonTypes.Submit}
                    text={isEdit ? 'Edit' : 'Create'}
                    disabled={!dirty || isSubmitting}
                  />
                </CommonStyles.FormButtons>
              }
            />
          </CommonStyles.Form>
        );
      }}
    </Formik>
  );
};
